import type { FormikProps, ScrollRegionHandle } from '@axiom/ui';
import React, { useMemo, useRef } from 'react';

import type { ChatForm as ChatFormValues } from '../../../schema/spark/chat-form-schema';
import { SparkSessionContext } from '../../hooks/useSparkSession';

import { useSparkStatus } from './SparkStatusProvider';
import type { SparkSession } from './spark-session.types';
import { SparkSendUtil } from './spark-send-util';
import { SparkSessionUtil } from './spark-session-util';
import { useSparkAttachments } from './useSparkAttachments';
import { useSparkConversation } from './useSparkConversation';
import { useSparkQueue } from './useSparkQueue';

export const SparkSessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const status = useSparkStatus();

  const chatFormRef = useRef<FormikProps<ChatFormValues> | null>(null);
  const scrollRegionRef = useRef<ScrollRegionHandle | null>(null);

  const shelf = useSparkAttachments();
  const queue = useSparkQueue();
  const transcript = useSparkConversation();

  const value = useMemo(() => {
    const session: SparkSession = {
      chatFormRef,
      queue,
      scrollRegionRef,
      shelf,
      status,
      transcript,
    };

    return {
      askAboutDraft: ({ body }: { body: string }) => {
        SparkSessionUtil.askAboutDraft({ body, session });
      },
      editQueuedPrompt: (promptId: string) => {
        SparkSessionUtil.editQueuedPrompt({ promptId, session });
      },
      selectChat: ({
        chatId,
        isFull,
      }: {
        chatId: string | null;
        isFull?: boolean;
      }) => {
        SparkSessionUtil.selectChat({ chatId, isFull, session });
      },
      session,
      startNewChat: () => {
        SparkSessionUtil.startNewChat({ session });
      },
      submitPrompt: async ({ chat }: Partial<ChatFormValues>) =>
        SparkSendUtil.submitPrompt({ chat, session }),
    };
  }, [queue, shelf, status, transcript]);

  return (
    <SparkSessionContext.Provider value={value}>
      {children}
    </SparkSessionContext.Provider>
  );
};
